<template>
	<div>
		<label
			class="flex items-center gap-1 py-1 text-xs hover:bg-muted/60 transition-colors cursor-pointer"
			:style="{ paddingLeft: (depth * 16 + 8) + 'px' }"
		>
			<!-- Expand/collapse toggle for nodes with children -->
			<button
				v-if="node.children?.length"
				@click.prevent.stop="$emit('toggle-expand', node.id)"
				class="shrink-0 w-4 h-4 flex items-center justify-center text-muted-foreground hover:text-foreground"
			>
				<ChevronRight v-if="!isExpanded" class="h-3 w-3" />
				<ChevronDown v-else class="h-3 w-3" />
			</button>
			<span v-else class="w-4 shrink-0" />

			<!-- Checkbox -->
			<input
				type="checkbox"
				:checked="selectedIds.includes(node.id)"
				@change.stop="$emit('toggle-select', node)"
				class="rounded border-input h-3.5 w-3.5 shrink-0"
			/>

			<!-- Label -->
			<span class="truncate flex-1">{{ node.label }}</span>

			<!-- Child count badge -->
			<span v-if="node.children?.length" class="text-[10px] text-muted-foreground shrink-0 pr-2">
				({{ node.children.length }})
			</span>
		</label>

		<!-- Recursive children -->
		<template v-if="isExpanded && node.children?.length">
			<MetaTreeNode
				v-for="(child, ci) in node.children"
				:key="'child-' + ci"
				:node="child"
				:depth="depth + 1"
				:field-key="fieldKey"
				:selected-ids="selectedIds"
				:expanded-ids="expandedIds"
				@toggle-select="$emit('toggle-select', $event)"
				@toggle-expand="$emit('toggle-expand', $event)"
			/>
		</template>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { ChevronRight, ChevronDown } from 'lucide-vue-next'

interface TreeNode {
	id: string | number
	label: string
	children?: TreeNode[]
	parentid?: string | number
	externalid?: string | number
}

const props = defineProps<{
	node: TreeNode
	depth: number
	fieldKey: string
	selectedIds: (string | number)[]
	expandedIds: (string | number)[]
}>()

defineEmits<{
	(e: 'toggle-select', payload: TreeNode): void
	(e: 'toggle-expand', id: string | number): void
}>()

const isExpanded = computed(() => props.expandedIds.includes(props.node.id))
</script>
